import React, { useState, useEffect, useRef } from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import { minSelectableDate, parseLocalDate } from "../../utils/dateUtils";
import Select from "react-select";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { currencyOptions } from "../../utils/currencyOptions";
import { read } from "xlsx";
import { groupIntegerDigits } from "../../utils/formatNumber";

const loanTypeOptions = [
  { value: "fixed", label: "Fixed" },
  { value: "variable", label: "Variable" },
  { value: "floating", label: "Floating" },
];

const repaymentOptions = [
  { value: "principalAndInterest", label: "Principal + Interest" },
  { value: "interestOnly", label: "Interest Only" },
];

const yesNoOptions = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// const currencyOptions = [
//   { value: "INR", label: "₹ INR" },
//   { value: "USD", label: "$ USD" },
//   { value: "EUR", label: "€ EUR" },
//   { value: "AUD", label: "A$ AUD" },
//   { value: "NZD", label: "NZ$ NZD" },
// ];

// Helper function to format number with commas
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const strValue = String(value).trim();
  if (strValue === "") return "";

  // Handle decimal numbers
  if (strValue.includes(".")) {
    const [integer, decimal] = strValue.split(".");
    const formattedInteger = groupIntegerDigits(integer); // ML-20: lakh/crore for INR users
    return `${formattedInteger}.${decimal}`;
  }

  return groupIntegerDigits(strValue);
};

// Helper function to parse formatted number back to numeric value
const parseFormattedNumber = (formattedValue) => {
  if (formattedValue === null || formattedValue === undefined || formattedValue === "") return "";
  const strValue = String(formattedValue).trim();
  if (strValue === "") return "";

  // Remove commas and return the clean numeric string
  return strValue.replace(/,/g, "");
};

// Custom Number Input Component with comma formatting
const FormattedNumberInput = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  disabled,
  readOnly,
}) => {
  const [displayValue, setDisplayValue] = useState("");
  const inputRef = useRef(null);

  // Sync display value when prop value changes externally
  useEffect(() => {
    if (value === null || value === undefined || value === "") {
      setDisplayValue("");
    } else {
      const formatted = formatNumberWithCommas(value);
      setDisplayValue(formatted);
    }
  }, [value]);

  const handleChange = (e) => {
    const inputValue = e.target.value;

    // Allow only digits, commas, and a single decimal point
    const cleanedValue = inputValue.replace(/[^\d.,]/g, "");

    // Prevent multiple decimal points
    const parts = cleanedValue.split(".");
    let sanitizedValue = parts[0];
    if (parts.length > 1) {
      sanitizedValue += "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (sanitizedValue.includes(".")) {
      const [integer, decimal] = sanitizedValue.split(".");
      sanitizedValue = `${integer}.${decimal.slice(0, 2)}`;
    }

    setDisplayValue(sanitizedValue);

    // Parse and call onChange with the numeric value
    const numericValue = parseFormattedNumber(sanitizedValue);
    let finalValue = "";

    if (numericValue) {
      // Preserve the raw numeric string during typing to support decimals
      if (numericValue.endsWith(".") || /\.\d{0,2}$/.test(numericValue)) {
        finalValue = numericValue;
      } else {
        const num = parseInt(numericValue, 10);
        finalValue = num > 0 ? num : "";
      }
    }

    onChange({
      target: {
        name,
        value: finalValue
      }
    });
  };

  const handleBlur = (e) => {
    // Format the display value on blur
    const numericValue = parseFormattedNumber(displayValue);
    if (numericValue) {
      const num = parseFloat(numericValue);
      if (!isNaN(num)) {
        const formatted = formatNumberWithCommas(num);
        setDisplayValue(formatted);
      }
    }
    if (onBlur) onBlur(e);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      name={name}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
};


export const loanFormInitialValues = {
  propertyAddress: "",
  lenderName: "",
  loanType: "",
  originalLoanAmount: "",
  originalLoanCurrency: "",
  outstandingAmount: "",
  loanStartDate: null,
  loanTerm: "",
  interestRate: "",
  monthlyRepayment: "",
  repaymentType: "",
  offsetAccountLinked: "",
  nextPaymentDate: null,
  maturityDate: null,
  name: "",
};

export const loanFormValidationSchema = Yup.object().shape({
  propertyAddress: Yup.string()
    .min(3, "Min length: 3 characters")
    .max(150, "Max length: 150 characters")
    .matches(
      /^[A-Za-z0-9\s,./#-]+$/,
      "Allowed: letters, numbers, spaces, and common symbols (, . - / #)"
    )
    .required("Property address is required"),
  lenderName: Yup.string()
    .min(3, "Min length: 3 characters")
    .max(100, "Max length: 100 characters")
    .matches(
      /^[A-Za-z0-9\s-.&]+$/,
      "Allowed: letters, numbers, spaces, and common symbols (- & .)"
    )
    .required("Lender name is required"),
  loanType: Yup.string().required("Loan type is required"),
  originalLoanAmount: Yup.number()
    .typeError("Must be a number")
    .required("Original loan amount is required")
    .positive("Must be greater than 0")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    ),
  originalLoanCurrency: Yup.string().required("Currency is required"),
  outstandingAmount: Yup.number()
    .typeError("Must be a number")
    .required("Outstanding balance is required")
    .positive("Must be greater than 0")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    )
    .test(
      "outstanding-vs-loan-amount",
      "Outstanding balance cannot be greater than the loan amount.",
      function(value) {
        const { originalLoanAmount } = this.parent;
        if (!value || !originalLoanAmount) return true;
        return Number(value) <= Number(originalLoanAmount);
      }
    ),
  loanStartDate: Yup.date().required("Loan start date is required").test(
    "loanStartDate-validation",
    "Loan start date must be before the next payment date.",
    function(value) {
      const { nextPaymentDate } = this.parent;
      if (!value || !nextPaymentDate) return true;
      return new Date(value) < new Date(nextPaymentDate);
    }
  ).test(
    "loanStartDate-past-validation",
    "Loan start date must be today or a past date.",
    function(value) {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const loanStart = new Date(value);
      loanStart.setHours(0, 0, 0, 0);
      return loanStart <= today;
    }
  ),
  loanTerm: Yup.number()
    .typeError("Must be a number")
    .required("Loan term is required")
    .positive("Must be greater than 0"),
  interestRate: Yup.number()
    .typeError("Must be a number")
    .required("Interest rate is required")
    .min(0, "Must be a positive number")
    .max(100, "Must be a number between 0 and 100")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    ),
  monthlyRepayment: Yup.number()
    .typeError("Must be a number")
    .required("Monthly repayment is required")
    .positive("Must be greater than 0")
    .test(
      "is-decimal",
      "Must have up to 2 decimal places",
      (value) => {
        if (!value) return true;
        const decimalPlaces = value.toString().split(".")[1];
        return !decimalPlaces || decimalPlaces.length <= 2;
      }
    )
    .test(
      "monthly-vs-amounts",
      "Monthly repayment cannot be greater than the outstanding balance or loan amount.",
      function(value) {
        const { originalLoanAmount, outstandingAmount } = this.parent;
        if (!value) return true;

        const numValue = Number(value);
        const numLoanAmount = Number(originalLoanAmount || 0);
        const numOutstanding = Number(outstandingAmount || 0);

        // Check against loan amount
        if (numLoanAmount > 0 && numValue > numLoanAmount) return false;

        // Check against outstanding balance
        if (numOutstanding > 0 && numValue > numOutstanding) return false;

        return true;
      }
    ),
  repaymentType: Yup.string().required("Repayment type is required"),
  offsetAccountLinked: Yup.string().required("Offset account status is required"),
  nextPaymentDate: Yup.date().required("Next payment date is required").test(
    "nextPaymentDate-validation",
    "Next payment date must be after the loan start date.",
    function(value) {
      const { loanStartDate } = this.parent;
      if (!value || !loanStartDate) return true;
      return new Date(value) > new Date(loanStartDate);
    }
  ),
  maturityDate: Yup.date().required("Maturity date is required").test(
    "maturityDate-start-validation",
    "Maturity date must be after the loan start date.",
    function(value) {
      const { loanStartDate } = this.parent;
      if (!value || !loanStartDate) return true;
      return new Date(value) > new Date(loanStartDate);
    }
  ).test(
    "maturityDate-nextPayment-validation",
    "Maturity date must be after the next payment date.",
    function(value) {
      const { nextPaymentDate } = this.parent;
      if (!value || !nextPaymentDate) return true;
      return new Date(value) > new Date(nextPaymentDate);
    }
  ),
  name: Yup.string()
    .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?=.*[A-Za-z])[A-Za-z0-9 .()\-]+$/,
        "Must contain at least one letter, allowed: letters, numbers, spaces, dot, hyphen, parentheses."
      )
      .required("Name is required"),

});

const MortgageForm = ({ values, setFieldValue, errors, touched, handleChange, localReadOnly }) => {
  // Blur only marks the field touched. Writing values.X back here used a stale render value and
  // erased dropdown picks on touch devices, where react-select blurs right after a selection (ML-30).
  const { setFieldTouched } = useFormikContext();
  // Helper function to check if field should show error (only when touched and has error)
  const shouldShowError = (fieldName) => {
    return errors[fieldName] && touched[fieldName];
  };


  return (
    <>
    <div className="col-md-12 mb-4">
            <label className="form-label">
              Liability Name<span className="text-danger">*</span>
            </label>
            <Field
              type="text"
              name="name"
              placeholder="e.g., Home Mortgage"
              className={`form-control ${shouldShowError('name') ? "is-invalid" : ""}`}
              onChange={(e) => {
                handleChange(e);
                setFieldValue("name", e.target.value, true);
              }}
              onKeyPress={() => setFieldValue("name", values.name, true)}
              readOnly={localReadOnly}
              isDisabled={localReadOnly}
            />
            <ErrorMessage name="name" component="div" className="invalid-feedback" />
          </div>
    <div className="row">
      {/* Property Address */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Property Address<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="propertyAddress"
          className={`form-control ${shouldShowError('propertyAddress') ? "is-invalid" : ""}`}
          placeholder="Enter property address"
          onChange={(e) => {
            handleChange(e);
            setFieldValue("propertyAddress", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("propertyAddress", values.propertyAddress, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="propertyAddress" component="div" className="invalid-feedback" />
      </div>

      {/* Lender Name */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Lender Name<span className="text-danger">*</span></label>
        <Field
          type="text"
          name="lenderName"
          className={`form-control ${shouldShowError('lenderName') ? "is-invalid" : ""}`}
          placeholder="Enter lender name"
          onChange={(e) => {
            handleChange(e);
            setFieldValue("lenderName", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("lenderName", values.lenderName, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="lenderName" component="div" className="invalid-feedback" />
      </div>

      {/* Loan Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Type<span className="text-danger">*</span></label>
        <Select
          options={loanTypeOptions}
          value={loanTypeOptions.find(option => option.value === values.loanType)}
          onChange={(option) => {
            setFieldValue("loanType", option.value);
            setFieldValue("loanType", option.value, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("loanType", true)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="loanType" component="div" className="text-danger small" />
      </div>

      {/* Original Loan Amount with Currency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Original Loan Amount<span className="text-danger">*</span></label>
        <div className="input-group">
          <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.originalLoanCurrency)}
            onChange={option => {
              setFieldValue("originalLoanCurrency", option.value);
              setFieldValue("originalLoanCurrency", option.value, true); // Trigger validation
            }}
            onBlur={() => setFieldTouched("originalLoanCurrency", true)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
            readOnly={localReadOnly}
            isDisabled={localReadOnly}
          />
          <FormattedNumberInput
            name="originalLoanAmount"
            value={values.originalLoanAmount}
            onChange={(e) => {
              setFieldValue("originalLoanAmount", e.target.value, true);
              // Trigger validation for dependent fields
              setTimeout(() => {
                setFieldValue("outstandingAmount", values.outstandingAmount, true);
                setFieldValue("monthlyRepayment", values.monthlyRepayment, true);
              }, 0);
            }}
            onBlur={() => setFieldTouched("originalLoanAmount", true)}
            placeholder="eg. 500,000"
            className={`form-control ${shouldShowError('originalLoanAmount') ? "is-invalid" : ""}`}
            disabled={localReadOnly}
            readOnly={localReadOnly}
          />
        </div>
        <ErrorMessage name="originalLoanAmount" component="div" className="invalid-feedback" />
        <ErrorMessage name="originalLoanCurrency" component="div" className="text-danger small" />
      </div>

      {/* Outstanding Balance */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Outstanding Balance<span className="text-danger">*</span></label>
        <FormattedNumberInput
          name="outstandingAmount"
          value={values.outstandingAmount}
          onChange={(e) => {
            setFieldValue("outstandingAmount", e.target.value, true);
            // Trigger validation for dependent field
            setTimeout(() => {
              setFieldValue("monthlyRepayment", values.monthlyRepayment, true);
            }, 0);
          }}
          onBlur={() => setFieldTouched("outstandingAmount", true)}
          placeholder="eg. 300,000"
          className={`form-control ${shouldShowError('outstandingAmount') ? "is-invalid" : ""}`}
          disabled={localReadOnly}
          readOnly={localReadOnly}
        />
        <ErrorMessage name="outstandingAmount" component="div" className="invalid-feedback" />
      </div>

      {/* Loan Start Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Start Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.loanStartDate}
          onChange={(date) => {
            setFieldValue("loanStartDate", date, true);
            // Clear a later date only if the new start date makes it invalid (ML-03).
            const start = parseLocalDate(date);
            if (start && parseLocalDate(values.nextPaymentDate) <= start) setFieldValue("nextPaymentDate", null);
            if (start && parseLocalDate(values.maturityDate) <= start) setFieldValue("maturityDate", null);
          }}
          onBlur={() => setFieldTouched("loanStartDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${shouldShowError('loanStartDate') ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={new Date()}
          renderCustomHeader={({
            date,
            changeMonth,
            changeYear,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => {
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth();
            const selectedYear = date && date.getFullYear ? date.getFullYear() : currentYear;

            const years = Array.from(
              { length: currentYear - 1900 + 1 },
              (_, i) => currentYear - i
            );

            const months = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];

            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                {/* Month arrows like the other date pickers (ML-09) */}
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 px-1"
                  aria-label="Previous month"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                >
                  &#8249;
                </button>
                <select
                  value={date && date.getMonth ? date.getMonth() : currentMonth}
                  onChange={(e) =>
                    changeMonth(Number(e.target.value))
                  }
                >
                  {months.map((month, index) => {
                    const disableMonth =
                      selectedYear > currentYear ||
                      (selectedYear === currentYear &&
                        index > currentMonth);

                    return (
                      <option
                        key={month}
                        value={index}
                        disabled={disableMonth}
                      >
                        {month}
                      </option>
                    );
                  })}
                </select>

                <select
                  value={selectedYear}
                  onChange={(e) =>
                    changeYear(Number(e.target.value))
                  }
                >
                  {years.map((year) => (
                    <option
                      key={year}
                      value={year}
                      disabled={year > currentYear}
                    >
                      {year}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 px-1"
                  aria-label="Next month"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                >
                  &#8250;
                </button>
              </div>
            );
          }}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="loanStartDate" component="div" className="invalid-feedback" />
      </div>

      {/* Loan Term */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Loan Term (years)<span className="text-danger">*</span></label>
        <Field
          type="number"
          name="loanTerm"
          className={`form-control ${shouldShowError('loanTerm') ? "is-invalid" : ""}`}
          placeholder="eg. 30"
          onChange={(e) => {
            handleChange(e);
            setFieldValue("loanTerm", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("loanTerm", values.loanTerm, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="loanTerm" component="div" className="invalid-feedback" />
      </div>

      {/* Interest Rate */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Interest Rate (%)<span className="text-danger">*</span></label>
        <Field
          type="number"
          step="0.01"
          name="interestRate"
          className={`form-control ${shouldShowError('interestRate') ? "is-invalid" : ""}`}
          placeholder="eg. 3.5"
          onChange={(e) => {
            handleChange(e);
            setFieldValue("interestRate", e.target.value, true);
          }}
          onKeyPress={() => setFieldValue("interestRate", values.interestRate, true)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="interestRate" component="div" className="invalid-feedback" />
      </div>

      {/* Monthly Repayment */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Monthly Repayment<span className="text-danger">*</span></label>
        <FormattedNumberInput
          name="monthlyRepayment"
          value={values.monthlyRepayment}
          onChange={(e) => setFieldValue("monthlyRepayment", e.target.value, true)}
          onBlur={() => setFieldTouched("monthlyRepayment", true)}
          placeholder="eg. 1,500"
          className={`form-control ${shouldShowError('monthlyRepayment') ? "is-invalid" : ""}`}
          disabled={localReadOnly}
          readOnly={localReadOnly}
        />
        <ErrorMessage name="monthlyRepayment" component="div" className="invalid-feedback" />
      </div>

      {/* Repayment Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Repayment Type<span className="text-danger">*</span></label>
        <Select
          options={repaymentOptions}
          value={repaymentOptions.find(option => option.value === values.repaymentType)}
          onChange={(option) => {
            setFieldValue("repaymentType", option.value);
            setFieldValue("repaymentType", option.value, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("repaymentType", true)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="repaymentType" component="div" className="text-danger small" />
      </div>

      {/* Offset Account Linked */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Offset Account Linked<span className="text-danger">*</span></label>
        <Select
          options={yesNoOptions}
          value={yesNoOptions.find(option => option.value === values.offsetAccountLinked)}
          onChange={(option) => {
            setFieldValue("offsetAccountLinked", option.value);
            setFieldValue("offsetAccountLinked", option.value, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("offsetAccountLinked", true)}
          className="react-select-container"
          classNamePrefix="react-select"
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="offsetAccountLinked" component="div" className="text-danger small" />
      </div>

      {/* Next Payment Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Next Payment Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.nextPaymentDate}
          onChange={(date) => {
            setFieldValue("nextPaymentDate", date);
            setFieldValue("nextPaymentDate", date, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("nextPaymentDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${shouldShowError('nextPaymentDate') ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={minSelectableDate(values.loanStartDate)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="nextPaymentDate" component="div" className="invalid-feedback" />
      </div>

      {/* Maturity Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">Maturity Date<span className="text-danger">*</span></label>
        <DatePicker
          selected={values.maturityDate}
          onChange={(date) => {
            setFieldValue("maturityDate", date);
            setFieldValue("maturityDate", date, true); // Trigger validation
          }}
          onBlur={() => setFieldTouched("maturityDate", true)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${shouldShowError('maturityDate') ? "is-invalid" : ""}`}
          placeholderText="Select date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          minDate={minSelectableDate(values.loanStartDate, values.nextPaymentDate)}
          readOnly={localReadOnly}
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="maturityDate" component="div" className="invalid-feedback" />
      </div>
    </div>
    </>
  );
};

export default MortgageForm;
